import { BCMSCurrency, BCMSDate, DisabledPrompt, useRoute } from "@bcm-dev/bcms-vite";
import { useActivityPageContext } from "compid/pages/ActivityPage";
import React from "react";

import ButtonContainer from 'compid/shared/containers/ButtonContainer';
import WidgetContainer from "compid/shared/containers/WidgetContainer";
import WidgetHeaderView from "compid/shared/containers/WidgetContainer/views/WidgetHeaderView";
import BillerView from "compid/shared/views/BillerView";
import BillerPaymentView from "compid/shared/views/BillerView/components/views/BillerPaymentView";
import { SVGAsset } from "res";

/**
 * @function AllActivityView
 * @returns {React.Component}
 * @description Shows a view for all payments when the user has clicked "Apply" in the Activity page.
 * This ui is different than what is shown by default in the Activity page since all filtered 
 * payments are shown together instead of being separated into distinct sections with distinct headers.
 * @exported true
 * @example
 * <AllActivityView />
 */
export default function AllActivityView(props) {
    // Call the useRoute hook and create the onClick event to occur when the edit button is clicked
    const navigate = useRoute();
    const viewPayment = id => () => navigate(`/viewPayment/${ id }`);
    // Grab state from the page context and determine if the state is an intance of the biller model class
    const { filters, filtered } = useActivityPageContext();
    const { status } = filters ?? {};
    let statusFormatted =  status ? status.toLowerCase() : "all";
    const payments = filtered;

    // If there are no payment items then this component should be hidden
    const shouldShowNoBills = payments.length === 0;

    // The header message is shown when there are results.
    let headerMessage = getHeaderMessage(filters, payments);

    // The footer message is shown when there are no results
    // when "all" or "processing" statuses are set.
    const footerMessage = getFooterMessage(filters, payments);
        
    // Map over all payments creating a BillerView component for each
    const paymentItems = payments.map(payment => {
        const rightChevron = payment.status === "processing" ? 
        <DisabledPrompt>
            <SVGAsset id="RightChevronIcon" className="clickable mobile-chevron" onClick={ viewPayment(payment.id) } data-point={ `Activity_Scheduled_Chevron_${ payment.id }` } />
        </DisabledPrompt> :
         <SVGAsset id="RightChevronIcon" className="clickable mobile-chevron" onClick={ viewPayment(payment.id) } data-point={ `Activity_Scheduled_Chevron_${ payment.id }` } />;

        return (
            <BillerView 
                key={ "payment_" + payment.id }
                biller={ payment.to }
                dataPoint={ `Activity_Scheduled_Biller_${ payment.id }` }
                paymentInfo={
                    <BillerPaymentView 
                        amount={ payment.amount }
                        status={ payment.status }
                        date={ payment.formatDate }
                        showAutopay={ payment.hasRelatedAutopay } />
                }
                buttons={
                    <ButtonContainer className="set-width">
                        { rightChevron }
                    </ButtonContainer>
                } />
        );
    }, []);
  
    return (
        <WidgetContainer>
            { shouldShowNoBills && statusFormatted === "processed" && 
                <WidgetHeaderView dataPoint="Activity_History_Header">
                    <p className="gray-text">{ `Total: ${ BCMSCurrency.toCurrency(0) }` }</p>
                </WidgetHeaderView>
            }
            { !shouldShowNoBills &&
                <WidgetHeaderView dataPoint="Activity_History_Header">
                    <p className="gray-text">{ headerMessage }</p>
                </WidgetHeaderView>
            }
            { shouldShowNoBills ? <NoBillsView status={ statusFormatted }/> : paymentItems }
            { shouldShowNoBills && 
            (statusFormatted === "all" || statusFormatted === "processed") &&
                <WidgetHeaderView>
                    <p className="gray-text">{ footerMessage }</p>
                </WidgetHeaderView>
            }
        </WidgetContainer>
    );
  }

    /**
     * @function getHeaderMessage
     * @returns {STRING}
     * @description Returns a string that either a beginning date
     * for the filtered payments or the total amount of the filtered payments.
     * @exported false
     * @example
     * const message = getHeaderMessage(filters, payments);
     */
    function getHeaderMessage(filters, payments) {
    const { custom, date, status } = filters ?? {};
    const statusFormatted = status ? status.toLowerCase() : "all";
    const dateValue = date ? date.value : 0;
    const startDate = custom ? custom.value.startDate : BCMSDate.init().offset(dateValue);
    let headerMessage = "";
    const hasPayments = payments.length > 0;

    // Calcuate the total amount based on the payments
    const totalAmount = payments.reduce((result, payment) => result + parseFloat(payment.amount.amount), 0);

    if((statusFormatted === "all" || statusFormatted === "processed") && hasPayments) {
        headerMessage = `Since ${ startDate.format("M d, Y") }`;
    } else if((statusFormatted === "all" || statusFormatted === "processed") && !hasPayments) {
        headerMessage = `From ${ startDate.format("M d, Y") }`;
    } else if((statusFormatted === "scheduled" || statusFormatted === "processing") && hasPayments) {
        headerMessage = `Total: ${ BCMSCurrency.toCurrency(totalAmount) }`;
    }

    return headerMessage;
    }

    /**
     * @function getFooterMessage
     * @returns {STRING} - Returns a footer message "Since [[date]]" only shown when there are no results 
     * when "all" or "processing" statuses are set.
     * @description Returns a footer message "Since [[date]]" only shown when there are no results 
     * when "all" or "processing" statuses are set.
     * @exported false
     * @example
     * const message = getHeaderMessage(filters, payments);
     */
    function getFooterMessage(filters, payments) {
        const { custom, date, status } = filters ?? {};
        const dateValue = date ? date.value : 0;
        const startDate = custom ? custom.value.startDate : BCMSDate.init().offset(dateValue);
        return `Since ${ startDate.format("M d, Y") }`;
    }

    /**
     * @function NoBillsView
     * @returns {React.Component}
     * @description Shows the ui for no bills founds.
     * @exported false
     * @example
     * <NoBillsView />
     */
  function NoBillsView(props) {
    let message = "No bills to sort.";
    let status = props.status ?? "all";

    switch(status) {
        case "processed":
            message = `No bill history found.`;
            break;
        case "scheduled":
            message = `No ${ status } bills found.`;
            break;
        case "processing":
            message = `No ${ status } bills found.`;
            break;
        default:
            break;
    }

    return (
        <div className="row-container">
            <div className="flex-container flex-horizontal align-center row-main-content">
                {/* <SVGAsset id="UpsellIcon" className="show-on-desktop blue-icon logo set-width logo-container" /> */}
                <SVGAsset id="UpsellIcon" className="logo set-width logo-container" />

                <div className="flex-width">
                    <span>{ message }</span>
                </div>
            </div>
        </div>
    );
}